import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Link2,
  Target,
  UserSquare2,
  QrCode,
  Globe,
  KeyRound,
  X,
  ArrowRight,
  Lock,
  Check,
  Copy,
} from "lucide-react";
import { CandyButton } from "../playful/CandyButton";
import { GeometricBadge } from "../playful/GeometricBadge";
import { useCreateLink } from "../../hooks/queries";
import { usePlan } from "../../hooks/usePlan";
import { useCopy } from "../../hooks/useCopy";
import { formatApiError } from "../../lib/api";

const OPTIONS = [
  {
    key: "campaign",
    icon: Target,
    tone: "pink",
    label: "Campaign",
    desc: "Group links + roll-up analytics",
    to: "/campaigns",
    gate: "pro",
  },
  {
    key: "bio",
    icon: UserSquare2,
    tone: "candy",
    label: "Bio Page",
    desc: "Your link-in-bio profile",
    to: "/bio/edit",
    gate: "pro",
  },
  {
    key: "qr",
    icon: QrCode,
    tone: "yellow",
    label: "QR Code",
    desc: "Branded QR for any link",
    to: "/links?create=1",
    gate: null,
  },
  {
    key: "domain",
    icon: Globe,
    tone: "mint",
    label: "Domain",
    desc: "Your own branded host",
    to: "/domains",
    gate: "pro",
  },
  {
    key: "apikey",
    icon: KeyRound,
    tone: "candy",
    label: "API Key",
    desc: "Programmatic access",
    to: "/api-keys",
    gate: "pro",
  },
];

const toneText = {
  candy: "text-candy",
  pink: "text-pink",
  yellow: "text-yellow",
  mint: "text-mint",
};
const toneTint = {
  candy: "bg-candy-tint",
  pink: "bg-pink-tint",
  yellow: "bg-yellow-tint",
  mint: "bg-mint-tint",
};

/** UniversalCreate — one place to create anything. Controlled modal. */
export default function UniversalCreate({ open, onClose }) {
  const navigate = useNavigate();
  const { isPro, isGrowth, isEnterprise } = usePlan();
  const createLink = useCreateLink();
  const [url, setUrl] = useState("");
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const { copied, copy: copyShort } = useCopy(issued?.short_url, 1000);

  const has = (gate) =>
    !gate ? true : gate === "enterprise" ? isEnterprise : gate === "growth" ? isGrowth : isPro;

  useEffect(() => {
    if (open) {
      setIssued(null);
      setUrl("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (opt) => {
    onClose();
    navigate(has(opt.gate) ? opt.to : "/pricing");
  };

  const quickShorten = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    try {
      const data = await createLink.mutateAsync({ url: url.trim() });
      setIssued(data);
      toast.success("Link created.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    copyShort();
    toast.success("Copied");
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center p-4 pt-[10vh]"
      aria-modal="true"
      role="dialog"
    >
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-[2px]" onClick={onClose} />
      <div
        className="pop-in relative w-full max-w-xl overflow-hidden rounded-3xl border-2 border-ink bg-white shadow-sticker-lg"
        data-testid="universal-create"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b-2 border-ink bg-candy-tint px-6 py-4">
          <GeometricBadge tone="candy" size="sm">
            CREATE
          </GeometricBadge>
          <button
            onClick={onClose}
            className="candy-press rounded-full border-2 border-ink bg-white p-1.5 shadow-sticker-xs"
            aria-label="Close"
          >
            <X size={14} strokeWidth={3} />
          </button>
        </div>

        <div className="p-6">
          {/* Quick link */}
          {issued ? (
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border-2 border-ink bg-mint-tint px-4 py-3 shadow-sticker-xs">
              <GeometricBadge tone="mint" size="sm">
                LANE OPENED
              </GeometricBadge>
              <a
                href={issued.short_url}
                target="_blank"
                rel="noreferrer"
                className="break-all font-display font-extrabold text-candy hover:underline"
              >
                {issued.short_url}
              </a>
              <div className="ml-auto flex gap-2">
                <button
                  onClick={copy}
                  aria-label={copied ? "Copied" : "Copy"}
                  className={`candy-press inline-flex items-center gap-1.5 rounded-full border-2 border-ink bg-white px-3 py-1.5 font-mono text-2xs uppercase tracking-[0.22em] shadow-sticker-xs ${copied ? "text-mint" : ""}`}
                >
                  {copied ? (
                    <Check size={12} strokeWidth={3} />
                  ) : (
                    <>
                      <Copy size={12} strokeWidth={3} /> Copy
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    onClose();
                    navigate("/links");
                  }}
                  className="candy-press inline-flex items-center gap-1.5 rounded-full border-2 border-ink bg-yellow px-3 py-1.5 font-mono text-2xs uppercase tracking-[0.22em] shadow-sticker-xs"
                >
                  Manage
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={quickShorten}>
              <label className="mb-2 block font-mono text-2xs uppercase tracking-[0.24em] text-muted-foreground">
                Quick shorten a link
              </label>
              <div className="flex items-center gap-2 rounded-2xl border-2 border-ink bg-white px-4 py-2.5 shadow-sticker-xs focus-within:shadow-sticker">
                <Link2 size={16} strokeWidth={2.5} className="shrink-0 text-candy" />
                <input
                  ref={inputRef}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Paste any long URL…"
                  className="min-w-0 flex-1 border-0 bg-transparent font-sans text-base placeholder:text-muted-foreground focus:outline-none"
                  data-testid="universal-create-url"
                />
                <CandyButton
                  type="submit"
                  variant="candy"
                  size="sm"
                  disabled={busy || !url.trim()}
                  withArrow
                >
                  {busy ? "…" : "Go"}
                </CandyButton>
              </div>
              <button
                type="button"
                onClick={() => go({ to: "/links?create=1", gate: null })}
                className="mt-2 inline-flex items-center gap-1 font-mono text-2xs uppercase tracking-[0.22em] text-candy hover:underline"
              >
                Advanced link options <ArrowRight size={11} strokeWidth={3} />
              </button>
            </form>
          )}

          {/* Create grid */}
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const locked = !has(opt.gate);
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => go(opt)}
                  data-testid={`universal-create-${opt.key}`}
                  className="sticker-hover group flex items-center gap-3 rounded-2xl border-2 border-ink bg-white px-4 py-3 text-left shadow-sticker-xs"
                >
                  <span
                    className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-2 border-ink ${toneTint[opt.tone]}`}
                  >
                    <Icon size={17} strokeWidth={2.5} className={toneText[opt.tone]} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 font-display text-sm font-bold">
                      {opt.label}
                      {locked && <Lock size={11} strokeWidth={2.5} className="text-ink/60" />}
                    </span>
                    <span className="block truncate font-sans text-xs text-muted-foreground">
                      {opt.desc}
                    </span>
                  </span>
                  <ArrowRight
                    size={14}
                    strokeWidth={3}
                    className="shrink-0 text-ink/60 transition-colors group-hover:text-candy"
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
